<script setup lang="ts">
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import { Button } from '@/components/ui/button'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
  } from '@/components/ui/command'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XMarkIcon
  } from '@heroicons/vue/24/outline'
  import { CheckIcon } from 'lucide-vue-next'
  import { computed, onMounted, ref, useSlots, watch } from 'vue'
  import CheckboxField from './form/CheckboxField.vue'

  export interface Filter {
    key: string
    label: string
    type: 'boolean' | 'number' | 'string' | 'select' | 'multi-select'
    value?: any
    options?: { label: string; value: any }[]
  }

  export interface FetchDataOptions {
    [key: string]: any

    limit?: number
    offset?: number
    search?: string
    filters?: Filter[]
  }

  export interface PaginatedResponse<T> {
    results: T[]
    next: string | null
    previous: string | null
    count: number
  }

  const props = defineProps<{
    fetchData: (options?: FetchDataOptions) => Promise<PaginatedResponse<any>>
    fetchOptions?: FetchDataOptions
    searchableFields?: string
    filterableFields?: Filter[]
    searchDebounce?: number
    selectable?: boolean
    selectedItems?: any[]
    paginationNavDataCy?: string
    paginationPrevDataCy?: string
    paginationNextDataCy?: string
    paginationInputDataCy?: string
  }>()

  const emit = defineEmits<{
    'update:selectedItems': [items: any[]]
  }>()

  const slots = useSlots()
  const columnCount = computed(() => {
    const headSlot = slots['table-head']
    if (headSlot) {
      const rendered = headSlot({})
      if (Array.isArray(rendered) && rendered.length > 0) {
        // Filter out any undefined or null elements and get real length
        const validElements = rendered.filter((el) => el != null)
        return validElements.length || 3
      }
    }
    return 3
  })

  const searchTerm = ref('')
  const filterSearchTerm = ref('')
  let searchDebounceTimeout: ReturnType<typeof setTimeout> | null = null

  const handleSearch = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    searchTerm.value = value

    const debounceTime = props.searchDebounce || 300

    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout)
    }

    searchDebounceTimeout = setTimeout(async () => {
      currentPage.value = 1
      await fetchData(currentPage.value)
    }, debounceTime)
  }

  watch(searchTerm, (newValue) => {})

  const paginatedData = ref<PaginatedResponse<any> | null>(null)
  const currentPage = ref(1)
  const inputPage = ref('1')
  const limit = computed(() => props.fetchOptions?.limit || 20)
  const filters = ref<Filter[]>([])
  const isLoading = ref(false)
  const isInitializing = ref(true)
  const selectedItems = ref<any[]>(props.selectedItems || [])
  const selectAll = ref(false)
  const userModifiedFilters = ref<Set<string>>(new Set())
  let latestRequestId = 0

  const totalPages = computed(() => {
    const count = paginatedData?.value?.count ?? 0
    return Math.ceil(count / limit.value)
  })

  const items = computed<any[]>(() => paginatedData.value?.results ?? [])
  const hasNext = computed(() => paginatedData.value?.next !== null)

  const fetchData = async (page: number) => {
    try {
      // Generate unique request ID for this request
      const requestId = ++latestRequestId

      const offset = (page - 1) * limit.value
      currentPage.value = page
      inputPage.value = page.toString()

      const response = await props.fetchData({
        limit: limit.value,
        offset,
        search: searchTerm.value,
        filters: filters.value,
        ...props.fetchOptions
      })

      // Only update data if this is still the latest request
      if (requestId === latestRequestId) {
        paginatedData.value = response
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      throw error
    }
  }

  const goToPage = () => {
    const pageNumber = parseInt(inputPage.value)
    if (pageNumber >= 1 && pageNumber <= totalPages.value) {
      handlePageChange(pageNumber)
    } else {
      // Reset input to current page if invalid
      inputPage.value = currentPage.value.toString()
    }
  }

  onMounted(async () => {
    initFilters()

    isLoading.value = true
    await fetchData(currentPage.value).finally(() => {
      isLoading.value = false
      isInitializing.value = false
    })
  })

  watch(
    () => props.filterableFields,
    (newFilterableFields) => {
      if (newFilterableFields) {
        filters.value = []
        initFilters()
      }
    },
    { deep: true }
  )

  watch(
    filters,
    async () => {
      // Skip fetching during initialization to prevent duplicate requests
      if (isInitializing.value) return
      isLoading.value = true
      await fetchData(1).finally(() => {
        isLoading.value = false
      })
    },
    { deep: true }
  )

  // Watch for changes to selectedItems prop
  watch(
    () => props.selectedItems,
    (newSelectedItems) => {
      selectedItems.value = newSelectedItems || []
      selectAll.value = selectedItems.value.length === items.value.length && items.value.length > 0
    },
    { deep: true }
  )

  const handlePageChange = async (page: number) => {
    isLoading.value = true
    await fetchData(page).finally(() => {
      isLoading.value = false
    })
  }

  const refreshData = () => {
    return fetchData(currentPage.value)
  }

  const toggleSelectAll = () => {
    selectAll.value = !selectAll.value
    if (selectAll.value) {
      selectedItems.value = [...items.value]
    } else {
      selectedItems.value = []
    }
    emit('update:selectedItems', selectedItems.value)
  }

  const toggleSelectItem = (item: any) => {
    const index = selectedItems.value.findIndex((selected) => selected.uuid === item.uuid)
    if (index > -1) {
      selectedItems.value.splice(index, 1)
    } else {
      selectedItems.value.push(item)
    }
    selectAll.value = selectedItems.value.length === items.value.length
    emit('update:selectedItems', selectedItems.value)
  }

  const isItemSelected = (item: any) => {
    return selectedItems.value.some((selected) => selected.uuid === item.uuid)
  }

  defineExpose({
    refreshData,
    loading: isLoading
  })

  const getFilterValue = (key: string) => {
    const filter = filters.value.find((filter: Filter) => filter.key === key)
    return filter?.value
  }

  const getFilterDisplayValue = (key: string) => {
    const filter = filters.value.find((filter: Filter) => filter.key === key)
    if (!filter) return ''

    // For select and multi-select filters, show the label instead of the value
    if ((filter.type === 'select' || filter.type === 'multi-select') && filter.options) {
      if (filter.type === 'select') {
        // Single select: find the matching option and return its label
        const option = filter.options.find((opt: any) => opt.value === filter.value)
        return option?.label || filter.value
      } else {
        // Multi-select: show comma-separated labels
        const values = filter.value ? filter.value.split(',') : []
        const labels = values.map((value: string) => {
          const option = filter.options?.find((opt: any) => opt.value === value)
          return option?.label || value
        })
        return labels.join(', ')
      }
    }

    // For boolean filters, show translated values
    if (filter.type === 'boolean') {
      return filter.value ? t('Yes') : t('No')
    }

    // For other filter types (number, string), show the value as is
    return filter.value
  }

  const removeFilterValue = async (key: string) => {
    const originalFilter = props.filterableFields?.find((filter) => filter.key === key)
    const filter = filters.value.find((filter) => filter.key === key)
    if (filter) {
      filter.value = originalFilter?.value
      userModifiedFilters.value.delete(key)
    }
  }

  const isFilterValueDifferentFromDefault = (filter: Filter) => {
    const originalFilter = props.filterableFields?.find((f) => f.key === filter.key)
    if (!originalFilter) return false

    if (userModifiedFilters.value.has(filter.key)) {
      return true
    }

    const currentValue = filter.value
    const defaultValue = originalFilter.value

    if (filter.type === 'boolean') {
      return currentValue !== defaultValue
    }

    if (filter.type === 'multi-select') {
      const currentValues = currentValue ? currentValue.split(',').sort() : []
      const defaultValues = defaultValue ? defaultValue.split(',').sort() : []
      return JSON.stringify(currentValues) !== JSON.stringify(defaultValues)
    }

    return currentValue !== defaultValue
  }

  const initFilters = () => {
    if (!props.filterableFields) return
    userModifiedFilters.value.clear()
    props.filterableFields.forEach((filter) => {
      filters.value.push({
        key: filter.key,
        value: filter.value,
        label: filter.label,
        type: filter.type,
        options: filter.options
      })
    })
  }

  const updateFilters = (event: Event, key: string, label: string, type: any) => {
    const target = event.target as HTMLInputElement
    const value = type === 'boolean' ? target.checked : target.value
    const filter = filters.value.find((filter) => filter.key === key)
    if (filter) {
      userModifiedFilters.value.add(key)

      // If multi-select, add or remove value from the string list
      if (type === 'multi-select') {
        if (!filter.value) filter.value = ''

        // Handle "__all__" option - toggle all other options
        if (value === '__all__') {
          const allOtherOptions =
            filter.options
              ?.filter((opt: any) => opt.value !== '__all__')
              .map((opt: any) => opt.value) || []

          if (target.checked) {
            // Select all options (excluding __all__ itself)
            filter.value = allOtherOptions.join(',')
          } else {
            // Deselect all
            filter.value = ''
          }
        } else {
          if (target.checked) {
            filter.value = filter.value ? `${filter.value},${value}` : value
          } else {
            filter.value = filter.value
              .replace(new RegExp(`(^|,)${value}(,|$)`, 'g'), ',') // Replace matched value with a comma
              .replace(/^,|,$/g, '') // Remove leading or trailing commas
          }
        }
      } else {
        filter.value = value ? value : undefined
      }
    }
  }

  // Helper to check if all options are selected (for __all__ checkbox state)
  const isAllSelected = (filter: Filter) => {
    if (filter.type !== 'multi-select' || !filter.options) return false
    const allOtherOptions = filter.options.filter((opt: any) => opt.value !== '__all__')
    const selectedValues = filter.value ? filter.value.split(',') : []
    return allOtherOptions.every((opt: any) => selectedValues.includes(opt.value))
  }

  // Helper to check if some (but not all) options are selected (for indeterminate state)
  const isSomeSelected = (filter: Filter) => {
    if (filter.type !== 'multi-select' || !filter.options) return false
    const allOtherOptions = filter.options.filter((opt: any) => opt.value !== '__all__')
    const selectedValues = filter.value ? filter.value.split(',') : []
    const selectedCount = allOtherOptions.filter((opt: any) =>
      selectedValues.includes(opt.value)
    ).length
    return selectedCount > 0 && selectedCount < allOtherOptions.length
  }

  // Helper to get count of selected items for multi-select filters
  const getSelectedCount = (filter: Filter) => {
    if (filter.type !== 'multi-select' || !filter.value) return 0
    return filter.value.split(',').filter((v: string) => v.length > 0).length
  }

  // Helper to get the "All Types" label for multi-select filters
  const getAllTypesLabel = (filter: Filter) => {
    if (!filter.options) return t('SelectAll')
    const allOption = filter.options.find((opt: any) => opt.value === '__all__')
    return allOption?.label || t('SelectAll')
  }

  const { t } = useTranslation({
    en: {
      Results: 'results',
      NoResultsFound: 'No results found',
      NoResultsFoundDescription:
        "Try adjusting your search or filter to find what you're looking for.",
      Of: 'of',
      Previous: 'Previous',
      Next: 'Next',
      Search: 'Search',
      ResetFilters: 'Reset Filters',
      Yes: 'Yes',
      No: 'No',
      SelectAll: 'Select All'
    },
    de: {
      Results: 'Einträge',
      NoResultsFound: 'Keine Einträge gefunden',
      NoResultsFoundDescription:
        'Versuchen Sie, Ihre Suche oder die Filterung anzupassen, um das Gesuchte zu finden.',
      Of: 'von',
      Previous: 'Zurück',
      Next: 'Weiter',
      Search: 'Suche nach',
      ResetFilters: 'Filter zurücksetzen',
      Yes: 'Ja',
      No: 'Nein',
      SelectAll: 'Alle auswählen'
    }
  })
</script>

<template>
  <div>
    <!-- Responsive search and filters section -->
    <div
      v-if="searchableFields || $slots.actions"
      class="grid gap-y-3 ps-1 pt-1 md:grid-cols-[1fr_auto] md:items-center md:gap-x-3 md:gap-y-0"
    >
      <!-- Search Input -->
      <div
        v-if="searchableFields"
        class="flex w-full flex-col gap-y-2 md:flex-row md:items-center md:gap-x-3"
      >
        <div class="relative w-full md:min-w-[200px] md:max-w-md">
          <div class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5">
            <svg
              class="size-4 flex-shrink-0 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              ></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>
          <input
            type="search"
            :placeholder="t('Search')"
            class="block w-full rounded-lg border border-gray-200 bg-gray-50 pe-4 ps-11 text-sm text-gray-800
              focus:border-purple-500 focus:ring-purple-500 dark:border-neutral-700 dark:bg-neutral-800
              dark:text-neutral-400 dark:placeholder:text-neutral-500 dark:focus:border-purple-500"
            :value="searchTerm"
            @input="handleSearch"
          />
        </div>
      </div>

      <!-- Filters and Actions -->
      <div
        v-if="filters || $slots.actions"
        class="flex w-full flex-col gap-y-2 md:flex-row md:items-center md:gap-x-3"
      >
        <!-- Filters -->
        <div
          v-if="filters"
          class="flex w-full flex-wrap gap-2 md:w-auto md:flex-nowrap md:gap-3"
        >
          <div
            v-for="filter in filters.filter((f) => f.type === 'boolean')"
            :key="filter.key"
            class="flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5"
          >
            <CheckboxField
              :name="filter.key"
              :label="filter.label"
              :modelValue="!!getFilterValue(filter.key)"
              class="truncate pl-1"
              @update:model-value="
                (value) =>
                  updateFilters(
                    { target: { checked: value } } as unknown as Event,
                    filter.key,
                    filter.label,
                    filter.type
                  )
              "
            />
          </div>

          <Popover
            v-for="filter in filters.filter((f) => f.type !== 'boolean')"
            :key="filter.key"
            class="w-full md:w-auto"
            @update:open="(open) => open && (filterSearchTerm = '')"
          >
            <PopoverTrigger as-child>
              <Button variant="secondary">
                <div class="relative flex items-center gap-1">
                  <span
                    class="text-md flex items-center hover:text-purple-600 focus:text-purple-600 focus:outline-none"
                  >
                    {{ filter.label }}
                  </span>
                  <!-- Count badge for multi-select filters (hidden when all selected) -->
                  <span
                    v-if="filter.type === 'multi-select' && !isAllSelected(filter)"
                    class="inline-flex min-w-[20px] items-center justify-center rounded-[20px] bg-purple-200 px-1 py-0.5
                      text-xs font-semibold text-purple-700"
                  >
                    {{ getSelectedCount(filter) }}
                  </span>
                  <ChevronDownIcon class="h-3 w-3" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              :align="'start'"
              class="w-[220px] rounded-xl p-0 shadow-[0px_4px_8px_rgba(0,0,0,0.08),0px_8px_12px_rgba(0,0,0,0.08)]"
            >
              <Command>
                <!-- Filter Item, depending on type -->
                <CommandList>
                  <CommandInput
                    v-if="filter.type === 'select'"
                    v-model="filterSearchTerm"
                    placeholder="Search options..."
                    class="my-1 h-9 border-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                  />
                  <CommandGroup>
                    <CommandItem
                      v-if="filter.type == 'number'"
                      :value="filter.key"
                      @select.prevent
                    >
                      <label class="cursor-pointer select-none text-sm font-medium text-gray-700">
                        {{ filter.label }}
                      </label>
                      <input
                        type="number"
                        class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        :value="getFilterValue(filter.key)"
                        @change="updateFilters($event, filter.key, filter.label, filter.type)"
                      />
                    </CommandItem>
                    <CommandItem
                      v-if="filter.type == 'string'"
                      :value="filter.key"
                      @select.prevent
                    >
                      <label class="cursor-pointer select-none text-sm font-medium text-gray-700">
                        {{ filter.label }}
                      </label>
                      <input
                        type="text"
                        class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        :value="getFilterValue(filter.key)"
                        @change="updateFilters($event, filter.key, filter.label, filter.type)"
                      />
                    </CommandItem>
                  </CommandGroup>
                  <CommandGroup v-if="filter.type == 'select'">
                    <CommandEmpty v-if="!filter.options?.length">No options found.</CommandEmpty>
                    <CommandItem
                      v-for="option in filter.options?.filter((opt) =>
                        opt.label.toLowerCase().includes(filterSearchTerm?.toLowerCase() || '')
                      )"
                      :key="option.value"
                      :value="option.label || ''"
                      :class="{
                        'selected-option bg-accent': getFilterValue(filter.key) === option.value
                      }"
                      @select="
                        updateFilters(
                          { target: { value: option.value } } as unknown as Event,
                          filter.key,
                          filter.label,
                          filter.type
                        )
                      "
                    >
                      <div class="flex w-full items-center justify-between">
                        <div class="flex items-center gap-2">
                          <span
                            :class="{
                              'text-gray-500': option.value === null,
                              'text-gray-800': option.value !== null
                            }"
                          >
                            {{ option.label }}
                          </span>
                        </div>
                        <CheckIcon
                          v-if="getFilterValue(filter.key) === option.value"
                          class="h-4 w-4 text-gray-600"
                        />
                      </div>
                    </CommandItem>
                  </CommandGroup>
                  <!-- Multi-select options -->
                  <CommandGroup
                    v-if="filter.type == 'multi-select'"
                    class="!p-0"
                  >
                    <!-- "Alle Typen" header -->
                    <div class="border-b border-gray-200 px-3 py-2.5">
                      <label class="flex h-5 cursor-pointer items-center gap-2">
                        <input
                          class="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          type="checkbox"
                          value="__all__"
                          :checked="isAllSelected(filter)"
                          :indeterminate="isSomeSelected(filter)"
                          @change="updateFilters($event, filter.key, filter.label, filter.type)"
                        />
                        <span class="text-sm font-medium leading-5 text-gray-800">
                          {{ getAllTypesLabel(filter) }}
                        </span>
                      </label>
                    </div>
                    <!-- Individual options -->
                    <div class="p-1.5">
                      <CommandEmpty v-if="!filter.options?.length">No options found.</CommandEmpty>
                      <CommandItem
                        v-for="option in filter.options?.filter(
                          (opt) =>
                            opt.value !== '__all__' &&
                            opt.label.toLowerCase().includes(filterSearchTerm?.toLowerCase() || '')
                        )"
                        :key="option.value"
                        class="w-full !rounded-md !px-1.5 !py-1.5"
                        :value="option.value"
                        @select.prevent
                      >
                        <label class="flex h-5 cursor-pointer items-center gap-2">
                          <input
                            class="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            type="checkbox"
                            :value="option.value"
                            :checked="getFilterValue(filter.key)?.includes(option.value)"
                            @change="updateFilters($event, filter.key, filter.label, filter.type)"
                          />
                          <span class="text-sm font-medium leading-5 text-gray-800">
                            {{ option.label }}
                          </span>
                        </label>
                      </CommandItem>
                    </div>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <!-- Actions -->
        <div
          v-if="$slots.actions"
          class="flex items-center gap-x-2"
        >
          <slot name="actions"></slot>
        </div>
      </div>
    </div>

    <!-- Filter Chips -->
    <div class="flex flex-wrap gap-2 pb-5">
      <div
        v-for="filter in filters.filter(
          (filter) => filter.value && isFilterValueDifferentFromDefault(filter)
        )"
        :key="filter.key"
        class="mt-2 flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-sm text-purple-600"
      >
        <span class="font-medium">
          {{ filter.label }}{{ filter.type === 'boolean' && filter.value === true ? '' : ':' }}
        </span>
        <span v-if="filter.type !== 'boolean' || filter.value !== true">
          {{ getFilterDisplayValue(filter.key) }}
        </span>
        <span
          v-if="getFilterValue(filter.key) !== undefined"
          class="cursor-pointer rounded"
          @click.stop="removeFilterValue(filter.key)"
        >
          <XMarkIcon class="h-5 w-5" />
        </span>
      </div>
    </div>

    <!-- Before table slot for banners, alerts, etc -->
    <slot name="before-table"></slot>

    <!-- Improved table container with proper responsive handling -->
    <div class="w-full overflow-hidden">
      <!-- Table with horizontal scroll for mobile -->
      <div class="w-full overflow-x-auto pb-2">
        <div v-if="isLoading">
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell
                  v-for="i in columnCount"
                  :key="i"
                >
                  <SkeletonLoader class="h-4 w-3/4" />
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow
                v-for="i in limit"
                :key="i"
              >
                <TableCell
                  v-for="j in columnCount"
                  :key="j"
                >
                  <SkeletonLoader class="h-4 w-3/4" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <Table
          v-else-if="items.length > 0"
          class="min-w-full divide-y divide-gray-200"
        >
          <TableHeader class="w-full bg-gray-50">
            <TableRow>
              <TableCell
                v-if="selectable"
                class="w-12 px-4 py-3.5"
              >
                <input
                  type="checkbox"
                  :checked="selectAll"
                  :indeterminate="selectedItems.length > 0 && selectedItems.length < items.length"
                  class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  @change="toggleSelectAll"
                />
              </TableCell>
              <slot name="table-head"></slot>
            </TableRow>
          </TableHeader>
          <TableBody>
            <slot
              name="table-row"
              :items="items"
              :toggle-select-item="toggleSelectItem"
              :is-item-selected="isItemSelected"
            ></slot>
          </TableBody>
        </Table>

        <div
          v-else
          class="flex min-h-[300px] flex-col items-center justify-center p-5 text-center md:min-h-[500px]"
        >
          <svg
            class="mx-auto mb-4 w-48"
            width="178"
            height="90"
            viewBox="0 0 178 90"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              class="fill-white dark:fill-neutral-800"
              x="27"
              y="50.5"
              width="124"
              height="39"
              rx="7.5"
              fill="currentColor"
            ></rect>
            <rect
              class="stroke-gray-50 dark:stroke-neutral-700/10"
              x="27"
              y="50.5"
              width="124"
              height="39"
              rx="7.5"
              stroke="currentColor"
            ></rect>
            <rect
              class="fill-gray-50 dark:fill-neutral-700/30"
              x="34.5"
              y="58"
              width="24"
              height="24"
              rx="4"
              fill="currentColor"
            ></rect>
            <rect
              class="fill-gray-50 dark:fill-neutral-700/30"
              x="66.5"
              y="61"
              width="60"
              height="6"
              rx="3"
              fill="currentColor"
            ></rect>
            <rect
              class="fill-gray-50 dark:fill-neutral-700/30"
              x="66.5"
              y="73"
              width="77"
              height="6"
              rx="3"
              fill="currentColor"
            ></rect>
            <rect
              class="fill-white dark:fill-neutral-800"
              x="19.5"
              y="28.5"
              width="139"
              height="39"
              rx="7.5"
              fill="currentColor"
            ></rect>
            <rect
              class="stroke-gray-100 dark:stroke-neutral-700/30"
              x="19.5"
              y="28.5"
              width="139"
              height="39"
              rx="7.5"
              stroke="currentColor"
            ></rect>
            <rect
              class="fill-gray-100 dark:fill-neutral-700/70"
              x="27"
              y="36"
              width="24"
              height="24"
              rx="4"
              fill="currentColor"
            ></rect>
            <rect
              class="fill-gray-100 dark:fill-neutral-700/70"
              x="59"
              y="39"
              width="60"
              height="6"
              rx="3"
              fill="currentColor"
            ></rect>
            <rect
              class="fill-gray-100 dark:fill-neutral-700/70"
              x="59"
              y="51"
              width="92"
              height="6"
              rx="3"
              fill="currentColor"
            ></rect>
            <g filter="url(#filter1)">
              <rect
                class="fill-white dark:fill-neutral-800"
                x="12"
                y="6"
                width="154"
                height="40"
                rx="8"
                fill="currentColor"
                shape-rendering="crispEdges"
              ></rect>
              <rect
                class="stroke-gray-100 dark:stroke-neutral-700/60"
                x="12.5"
                y="6.5"
                width="153"
                height="39"
                rx="7.5"
                stroke="currentColor"
                shape-rendering="crispEdges"
              ></rect>
              <rect
                class="fill-gray-200 dark:fill-neutral-700"
                x="20"
                y="14"
                width="24"
                height="24"
                rx="4"
                fill="currentColor"
              ></rect>
              <rect
                class="fill-gray-200 dark:fill-neutral-700"
                x="52"
                y="17"
                width="60"
                height="6"
                rx="3"
                fill="currentColor"
              ></rect>
              <rect
                class="fill-gray-200 dark:fill-neutral-700"
                x="52"
                y="29"
                width="106"
                height="6"
                rx="3"
                fill="currentColor"
              ></rect>
            </g>
            <defs>
              <filter
                id="filter1"
                x="0"
                y="0"
                width="178"
                height="64"
                filterUnits="userSpaceOnUse"
                color-interpolation-filters="sRGB"
              >
                <feFlood
                  flood-opacity="0"
                  result="BackgroundImageFix"
                ></feFlood>
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                ></feColorMatrix>
                <feOffset dy="6"></feOffset>
                <feGaussianBlur stdDeviation="6"></feGaussianBlur>
                <feComposite
                  in2="hardAlpha"
                  operator="out"
                ></feComposite>
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0"
                ></feColorMatrix>
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_1187_14810"
                ></feBlend>
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_1187_14810"
                  result="shape"
                ></feBlend>
              </filter>
            </defs>
          </svg>

          <div class="mx-auto max-w-sm">
            <p class="mt-2 font-medium text-gray-800 dark:text-neutral-200">
              {{ t('NoResultsFound') }}
            </p>
            <p class="mb-5 text-sm text-gray-500 dark:text-neutral-500">
              {{ t('NoResultsFoundDescription') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Responsive Pagination -->
    <div
      v-if="items.length > 0"
      class="mt-4 flex flex-col-reverse gap-y-3 sm:grid sm:grid-cols-2 sm:items-center sm:gap-x-5"
    >
      <!-- Results Count -->
      <p class="text-sm text-gray-800">
        <span
          data-cy="pagination-results-count"
          class="font-medium"
        >
          {{ paginatedData?.count }}
        </span>
        <span class="ps-2 text-gray-500">{{ t('Results') }}</span>
      </p>

      <!-- Navigation Controls -->
      <nav
        :data-cy="props.paginationNavDataCy"
        class="flex items-center justify-end gap-x-1"
      >
        <button
          :data-cy="props.paginationPrevDataCy"
          class="inline-flex min-h-[38px] min-w-[38px] items-center justify-center gap-x-2 rounded-lg px-2.5 py-2
            text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          :disabled="currentPage === 1"
          type="button"
          @click="handlePageChange(currentPage - 1)"
        >
          <ChevronLeftIcon class="mt-0.5 size-3.5 flex-shrink-0" />
          <span
            class="sr-only"
            aria-hidden="true"
          >
            {{ t('Previous') }}
          </span>
        </button>
        <div class="flex items-center gap-x-1">
          <input
            :data-cy="props.paginationInputDataCy"
            v-model="inputPage"
            class="flex h-8 w-12 items-center justify-center rounded-lg border-gray-200 bg-gray-50 px-3 py-2 text-sm
              text-gray-800 ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
            type="text"
            @keyup.enter="goToPage"
          />
          <span
            class="flex min-h-[38px] items-center justify-center px-1.5 py-2 text-sm text-gray-500"
          >
            {{ t('Of') }}
          </span>
          <span
            class="flex min-h-[38px] items-center justify-center px-1.5 py-2 text-sm text-gray-500"
          >
            {{ totalPages }}
          </span>
        </div>
        <button
          :data-cy="props.paginationNextDataCy"
          class="inline-flex min-h-[38px] min-w-[38px] items-center justify-center gap-x-2 rounded-lg px-2.5 py-2
            text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          :disabled="!hasNext"
          type="button"
          @click="handlePageChange(currentPage + 1)"
        >
          <span
            class="sr-only"
            aria-hidden="true"
          >
            {{ t('Next') }}
          </span>
          <ChevronRightIcon class="mt-0.5 size-3.5 flex-shrink-0" />
        </button>
      </nav>
    </div>
  </div>
</template>
